<template>
	<div>
		<v-card>
			<v-card-title
				class="d-flex align-center"
			>
				<v-icon
					icon="$lightning"
					start
				/>
				<translate>Claims</translate>
			</v-card-title>
			<v-card-text>
				<v-row
					v-can="'export'"
				>
					<v-spacer />
					<v-col cols="auto">
						<v-btn
							v-if="hasDetailsPermission"
							href="/bo/report/claims/details"
							append-icon="$export"
						>
							<translate>Download Overview</translate>
						</v-btn>
						<v-btn
							v-else-if="hasOverviewPermission"
							href="/bo/report/claims/overview"
							append-icon="$export"
						>
							<translate>Download Overview</translate>
						</v-btn>
					</v-col>
					<v-col cols="auto">
						<v-btn
							href="/bo/claims/status"
							append-icon="$transition"
						>
							<translate>Status Transitions Overview</translate>
						</v-btn>
					</v-col>
				</v-row>
				<v-row>
					<v-col>
						<v-select
							v-model="typeFilter"
							:items="types"
							item-title="text"
							:label="`${$gettext('Type')} ${$gettext('filter')}`"
							multiple
							clearable
							variant="outlined"
						/>
					</v-col>
					<v-col>
						<v-select
							v-model="domainFilter"
							:items="domains"
							item-title="text"
							:label="`${$gettext('Domain')} ${$gettext('filter')}`"
							multiple
							clearable
							variant="outlined"
						/>
					</v-col>
					<v-col>
						<v-select
							v-model="statusFilter"
							:items="statuses"
							item-title="text"
							:label="`${$gettext('Status')} ${$gettext('filter')}`"
							multiple
							clearable
							variant="outlined"
						/>
					</v-col>
				</v-row>
				<v-row>
					<v-col>
						<model-backend-table
							v-model="items"
							v-model:options="options"
							endpoint="/bo/claims/table"
							:headers="tableHeaders"
							:filter="filter"
							:items-per-page="25"
							:items-per-page-options="[25, 50, 100]"
						>
							<template
								v-for="(_, slot) of $slots"
								#[slot]="scope"
							>
								<slot
									:name="slot"
									v-bind="scope"
								/>
							</template>
							<template #item.identifier="{ item }">
								<a :href="`/bo/claims/view?id=${item.id}`">
									{{ item.identifier }}
								</a>
							</template>
							<template #item.name="{ item }">
								<a :href="`/bo/claims/view?id=${item.id}`">
									{{ item.name }}
								</a>
							</template>
							<template #item.claimtype="{ item }">
								{{ getItemTypeText(item.claimtype) }}
							</template>
							<template #item.updatedAt="{ item }">
								{{ item.updatedAt }}
								<span v-if="item.latestStatusChange">
									({{ item.latestStatusChange }} <translate>days ago</translate>)
								</span>
							</template>
							<template #item.claimStatus="{ item }">
								<translate>{{ item.claimStatus }}</translate>
							</template>
							<template #item.autoMail="{ item }">
								<v-icon
									v-if="item.autoMail"
									icon="$check"
								/>
							</template>
						</model-backend-table>
					</v-col>
				</v-row>
			</v-card-text>
		</v-card>
	</div>
</template>

<script>

import {
	axios,
} from '../../../../services/axios-service.js';

export default {
	name: 'ClaimsOverview',

	props: {
		hasDetailsPermission: {
			type: Boolean,
			required: true,
		},
		hasOverviewPermission: {
			type: Boolean,
			required: true,
		},
	},

	data () {
		return {
			loading: true,
			items: [],
			typeFilter: [],
			domainFilter: [],
			statusFilter: [],
			domains: [],
			statuses: [],
			options: {
			},
			totalItems: 0,
			sortBy: 'created_at',
			sortDesc: true,
		};
	},

	computed: {
		tableHeaders () {
			const headers = [
				{
					title: this.$gettext('Claim number'), value: 'identifier', sortable: false, filterable: false,
				},
				{
					title: this.$gettext('Name'), value: 'name', sortable: false, filterable: false,
				},
				{
					title: this.$gettext('Type'), value: 'claimtype', sortable: false, filterable: true,
					filter: (value) => {
						return this.typeFilter.length === 0 || this.typeFilter.includes(value);
					}
				},
				{
					title: this.$gettext('Created'), value: 'createdAt', sortable: true, filterable: false,
				},
				{
					title: this.$gettext('Last modification') + ' (' + this.$gettext('latest status change') + ')', value: 'updatedAt', sortable: true, filterable: false,
				},
				{
					title: this.$gettext('Insurance'), value: 'productName', sortable: false, filterable: false,
				},
				{
					title: this.$gettext('Domain'), value: 'domainName', sortable: false, filterable: true,
					filter: (value) => {
						return this.domainFilter.length === 0 || this.domainFilter.includes(value);
					}
				},
				{
					title: this.$gettext('Status'), value: 'claimStatus', sortable: false, filterable: true,
					filter: (value) => {
						return this.statusFilter.length === 0 || this.statusFilter.includes(value);
					}
				},
			];

			if (this.typeFilter.length === 1 && this.typeFilter[0] === 'damage' && this.statusFilter.length === 1 && this.statusFilter[0] === 0) {
				headers.push({
					title: this.$gettext('Auto mail status New'), value: 'autoMail', sortable: true, filterable: true,
				});
			}

			return headers;
		},

		types () {
			return [
				{
					text: this.$gettext('Damage'), value: 'damage'
				},
				{
					text: this.$gettext('Theft') + '/' + this.$gettext('loss'), value: 'loss'
				},
			];
		},

		/**
		 *
		 * @returns {object}
		 */
		filter: function () {
			return {
				'claimtype': this.typeFilter,
				'domain': this.domainFilter,
				'statuses': this.statusFilter
			};
		}
	},

	mounted () {
		this.getDomains();
		this.getStatuses();

	},

	methods: {
		/**
		 * @param {string} type
		 * @returns {string}
		 */
		getItemTypeText (type) {
			if (type === 'loss') {
				return this.$gettext('Theft') + '/' + this.$gettext('loss');
			}
			return this.$gettext('Damage');

		},

		/**
		 * @returns {void}
		 */
		getDomains () {
			axios.get('/api/v0/domains')
				.then(({
					data
				}) => {
					data.forEach((domain) => {
						this.domains.push(domain.name);
					});
				});
		},

		/**
		 * @returns {void}
		 */
		getStatuses () {
			axios.get('/api/v0/claimstatuses')
				.then(({
					data
				}) => {
					data.forEach((status) => {
						this.statuses.push(
							{
								text: this.$gettext(status.name), value: status.id
							}
						);
					});
				});
		},
	}
};
</script>
